import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TIERS = [
  {
    id: "diy",
    name: "DIY Package",
    price: "$399",
    tag: "Application review",
    for: "Students who just want expert eyes on their application.",
    features: [
      "1:1 written application review",
      "Personal statement feedback",
      "University shortlist sanity check",
      "Timeline check-in",
    ],
    highlight: false,
  },
  {
    id: "standard",
    name: "Standard",
    price: "$1,200",
    tag: "Hands-on help",
    for: "Students who want hands-on help through the whole admin process.",
    features: [
      "Everything in DIY",
      "Application support (drafts, revisions)",
      "Housing search assistance",
      "Visa guidance & Campus France",
      "Ongoing email support until arrival",
    ],
    highlight: false,
  },
  {
    id: "vip",
    name: "VIP",
    price: "$2,500+",
    tag: "End-to-end concierge",
    for: "Families who want the entire move handled end-to-end.",
    features: [
      "Everything in Standard",
      "Dedicated 1:1 appointments (weekly cadence)",
      "Airport pickup on arrival",
      "Move-in day assistance (lease, keys, utilities)",
      "CAF + banking + SIM setup done with you",
      "Direct WhatsApp line to your advisor",
    ],
    highlight: true,
  },
];

export default function Pricing() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", goal: "vip", when: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [socialProof, setSocialProof] = useState(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/stats/social-proof");
        if (!ignore) setSocialProof(data);
      } catch {}
    })();
    return () => { ignore = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setSending(true);
    try {
      await api.post("/concierge", form);
      toast.success("Thanks, Rose will send your invoice within one working day.");
      setForm({ name: "", email: "", phone: "", goal: "vip", when: "", notes: "" });
      // optimistic bump
      setSocialProof((sp) => sp ? { ...sp, leads_this_month: (sp.leads_this_month || 0) + 1 } : sp);
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally { setSending(false); }
  };

  const proofText = () => {
    if (!socialProof) return null;
    const n = socialProof.leads_this_month || 0;
    const month = socialProof.month || "this month";
    if (n <= 0) return `Now booking Fall 2026, limited 1:1 spots for ${month}`;
    if (n === 1) return `1 family booked in ${month}, a few 1:1 spots left`;
    return `${n} families booked in ${month}, a few 1:1 spots left`;
  };

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-pricing">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="rule-gold" /><span className="eyebrow">Concierge packages</span>
        </div>
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <h1 className="md:col-span-7 serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Choose <em className="italic text-[#b8892b]">how much</em> we do together.
          </h1>
          <p className="md:col-span-5 text-lg text-[#4a5570] leading-relaxed">
            Everyone gets the advisor, the guides, and the tracker for free. These packages add human hours from
            people who've done this fifty times.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 relative">
          {/* Sticky social proof pill */}
          <AnimatePresence>
            {socialProof && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="md:col-span-3 sticky top-24 z-10 -mb-2 flex justify-center"
                data-testid="social-proof-pill"
              >
                <div className="inline-flex items-center gap-2 bg-[#0d1b3e] text-white px-4 py-2 border border-[#b8892b]/50 shadow-lg">
                  <span className="relative flex w-2 h-2">
                    <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-[#b8892b] opacity-60" />
                    <span className="relative inline-flex rounded-full w-2 h-2 bg-[#b8892b]" />
                  </span>
                  <Users size={14} className="text-[#b8892b]" />
                  <span className="text-[11px] uppercase tracking-[0.18em] font-medium" data-testid="social-proof-text">
                    {proofText()}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {TIERS.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }}
              viewport={{ once:true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`relative p-8 flex flex-col ${t.highlight ? "bg-[#0d1b3e] text-white -mt-4 md:-mt-8 shadow-2xl" : "bg-white text-[#0d1b3e] border border-[#0d1b3e]/10"}`}
              data-testid={`pricing-tier-${t.id}`}>
              {t.highlight && (
                <div className="absolute top-0 right-0 bg-[#b8892b] text-white text-[10px] uppercase tracking-[0.22em] px-3 py-1">
                  Most popular
                </div>
              )}
              <div className={`eyebrow ${t.highlight ? "!text-[#b8892b]" : ""}`}>{t.tag}</div>
              <div className={`serif text-4xl mt-4 ${t.highlight ? "text-white" : ""}`}>{t.name}</div>
              <div className={`serif text-6xl mt-4 ${t.highlight ? "text-[#b8892b]" : "text-[#b8892b]"}`}>{t.price}</div>
              <p className={`mt-4 text-sm ${t.highlight ? "text-white/70" : "text-[#4a5570]"}`}>{t.for}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={16} className={`mt-0.5 shrink-0 ${t.highlight ? "text-[#b8892b]" : "text-[#b8892b]"}`} />
                    <span className={t.highlight ? "text-white/85" : "text-[#0d1b3e]/80"}>{f}</span>
                  </li>
                ))}
              </ul>
              <a href="#concierge-form" onClick={() => setForm(f => ({ ...f, goal: t.id }))}
                className={`btn-sharp mt-8 justify-center ${t.highlight ? "btn-sharp-gold" : ""}`}
                data-testid={`pricing-cta-${t.id}`}>
                Request {t.name} invoice
              </a>
            </motion.div>
          ))}
        </div>

        <div id="concierge-form" className="mt-24 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow mb-3">Talk to Rose</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight text-[#0d1b3e]">
              Request an invoice. Rose replies within one working day.
            </h2>
            <p className="mt-4 text-[#4a5570]">
              Tell us which package you want and Rose will send a personal invoice by email, no card details taken here.
              Prefer voice? WhatsApp <a className="underline decoration-[#b8892b]" href="https://wa.me/33618797689">+33 6 18 79 76 89</a> or email <a className="underline decoration-[#b8892b]" href="mailto:francepathfr@gmail.com">francepathfr@gmail.com</a>.
            </p>
          </div>
          <form onSubmit={submit} className="md:col-span-7 bg-white border border-[#0d1b3e]/10 p-8 space-y-4" data-testid="concierge-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Name</label>
                <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  data-testid="concierge-name"
                  className="mt-1 w-full border border-[#0d1b3e]/20 px-3 py-2 bg-white outline-none focus:border-[#b8892b]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  data-testid="concierge-email"
                  className="mt-1 w-full border border-[#0d1b3e]/20 px-3 py-2 bg-white outline-none focus:border-[#b8892b]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Phone (optional)</label>
                <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                  data-testid="concierge-phone"
                  className="mt-1 w-full border border-[#0d1b3e]/20 px-3 py-2 bg-white outline-none focus:border-[#b8892b]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Package</label>
                <select value={form.goal} onChange={(e) => setForm({...form, goal: e.target.value})}
                  data-testid="concierge-goal"
                  className="mt-1 w-full border border-[#0d1b3e]/20 px-3 py-2 bg-white outline-none focus:border-[#b8892b]">
                  <option value="diy">DIY, $399</option>
                  <option value="standard">Standard, $1,200</option>
                  <option value="vip">VIP, $2,500+</option>
                  <option value="unsure">Not sure yet</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Target start (e.g. Fall 2026)</label>
              <input value={form.when} onChange={(e) => setForm({...form, when: e.target.value})}
                data-testid="concierge-when"
                className="mt-1 w-full border border-[#0d1b3e]/20 px-3 py-2 bg-white outline-none focus:border-[#b8892b]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Notes</label>
              <textarea rows={4} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                data-testid="concierge-notes"
                className="mt-1 w-full border border-[#0d1b3e]/20 px-3 py-2 bg-white outline-none focus:border-[#b8892b]" />
            </div>
            <button type="submit" disabled={sending} data-testid="concierge-submit"
              className="btn-sharp btn-sharp-gold w-full justify-center">
              {sending ? "Sending…" : "Request invoice"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
